import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Camera, Plus, Zap, Target, Clock } from 'lucide-react';
import { MobileMealLogger } from './MobileMealLogger';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileQuickActionsProps {
  onLogMeal?: () => void;
  onPhotoAnalysis?: () => void;
  onQuickAdd?: () => void;
  onViewGoals?: () => void;
}

export function MobileQuickActions({ 
  onLogMeal, 
  onPhotoAnalysis, 
  onQuickAdd, 
  onViewGoals 
}: MobileQuickActionsProps = {}) {
  const isMobile = useIsMobile();

  const getCurrentMealSuggestion = () => {
    const hour = new Date().getHours();
    if (hour < 10) return { meal: 'Breakfast', icon: '🌅', time: 'Morning' };
    if (hour < 14) return { meal: 'Lunch', icon: '☀️', time: 'Afternoon' };
    if (hour < 17) return { meal: 'Snack', icon: '🍎', time: 'Afternoon' };
    return { meal: 'Dinner', icon: '🌙', time: 'Evening' };
  };

  const suggestion = getCurrentMealSuggestion();

  return (
    <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
      <CardContent className="p-4">
        {/* Time-based suggestion */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">
              {suggestion.time} • Time for {suggestion.meal}
            </span>
            <span className="text-lg">{suggestion.icon}</span>
          </div>
          <Badge variant="secondary" className="text-xs">
            Suggested
          </Badge>
        </div>

        {/* Quick Action Buttons */}
        <div className="space-y-3">
          {/* Primary Action - Mobile optimized meal logger */}
          <div className="flex gap-3">
            <MobileMealLogger onLogMeal={onLogMeal} />
            
            <Button 
              variant="outline" 
              size="lg" 
              onClick={onPhotoAnalysis}
              className={`nutrition-button-outline h-12 ${isMobile ? 'flex-1' : 'px-6'} text-base`}
            >
              <Camera className="h-5 w-5 mr-2" />
              Photo Analysis
            </Button>
          </div>

          {/* Secondary Actions - Compact row for mobile */}
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="ghost" 
              size={isMobile ? "sm" : "default"}
              onClick={onQuickAdd || onLogMeal}
              className="justify-start h-10 text-sm"
            >
              <Zap className="h-4 w-4 mr-2" />
              Quick Add
            </Button>
            
            <Button 
              variant="ghost" 
              size={isMobile ? "sm" : "default"}
              onClick={onViewGoals}
              className="justify-start h-10 text-sm"
            >
              <Target className="h-4 w-4 mr-2" />
              View Goals
            </Button>
          </div>
        </div>

        {/* Mobile-specific tip */}
        {isMobile && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              💡 <strong>Tip:</strong> Tap "Log Meal" for a streamlined mobile experience with quick food suggestions
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}